import { useMemo, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Copy } from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import type { DevMerchantResultEntry } from "@cpos/shared/src/dev-merchant/spec";
import type { JobRecord, DevMerchantCounters } from "@/application/tools/types";
import { merchantPath } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { JobStatusBadge } from "../components/JobStatusBadge";
import { MerchantsRepository } from "@/application/merchants/merchants_repository";
import { PurgeMerchantDialog } from "@/presentation/merchants/components/PurgeMerchantDialog";

interface Props {
  job: JobRecord;
  createConnectedAccount: boolean;
  onReset: () => void;
}

/** Ordered build phases, matching the dev-merchant-create job runner. */
const ALL_PHASES = [
  "stripe",
  "cognito",
  "records",
  "lifecycle",
  "entities",
  "finalize",
] as const;
type Phase = (typeof ALL_PHASES)[number];

function expectedPhases(createConnectedAccount: boolean): Phase[] {
  return createConnectedAccount
    ? [...ALL_PHASES]
    : ALL_PHASES.filter((p) => p !== "stripe");
}

function ProgressBar({
  processed,
  total,
}: {
  processed: number;
  total: number | null;
}) {
  const counting = total === null;
  const pct = total && total > 0 ? Math.min(100, (processed / total) * 100) : 0;

  let leftLabel: string;
  if (counting) leftLabel = "Starting…";
  else if (total === 0) leftLabel = "0 to process";
  else leftLabel = `${processed} of ${total} processed`;

  return (
    <div className="space-y-1">
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>{leftLabel}</span>
        {!counting && total !== 0 && <span>{Math.round(pct)}%</span>}
      </div>
      <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
        {counting ? (
          <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-400" />
        ) : total === 0 ? (
          <div className="bg-muted h-full w-full rounded-full" />
        ) : (
          <div
            className="h-full rounded-full bg-blue-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  );
}

const COUNTER_LABELS: Record<keyof DevMerchantCounters, string> = {
  created: "Created",
  failed: "Failed",
};
const COUNTER_KEYS = Object.keys(
  COUNTER_LABELS,
) as (keyof DevMerchantCounters)[];

function parseCounters(
  raw: string | null,
): Partial<DevMerchantCounters> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<DevMerchantCounters>;
  } catch {
    return null;
  }
}

function CounterGrid({ raw }: { raw: string | null }) {
  const counters = parseCounters(raw);
  if (!counters) return null;

  const entries = COUNTER_KEYS.map((key) => ({
    key,
    // eslint-disable-next-line security/detect-object-injection -- key is a member of the COUNTER_KEYS const array
    label: COUNTER_LABELS[key],
    // eslint-disable-next-line security/detect-object-injection -- key is a member of the COUNTER_KEYS const array
    value: counters[key] ?? 0,
  })).filter(({ value }) => value > 0);

  if (entries.length === 0) return null;

  return (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
      {entries.map(({ key, label, value }) => (
        <div
          key={key}
          className="border-border bg-muted rounded-md border px-3 py-2"
        >
          <p className="text-muted-foreground text-xs">{label}</p>
          <p className="text-foreground mt-0.5 text-lg font-semibold">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

function parseResult(raw: string | null): DevMerchantResultEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { merchants?: DevMerchantResultEntry[] };
    return Array.isArray(parsed.merchants) ? parsed.merchants : [];
  } catch {
    return [];
  }
}

function copy(label: string, value: string) {
  void navigator.clipboard.writeText(value);
  toast.success(`${label} copied`);
}

function PhaseChecklist({
  phases,
  failedPhase,
  allDone,
  building,
}: {
  phases: Phase[];
  failedPhase?: string;
  allDone: boolean;
  /** When true, `failedPhase` names the phase currently in progress, not a failure. */
  building?: boolean;
}) {
  const markedIndex = failedPhase
    ? phases.findIndex((p) => p === failedPhase)
    : -1;

  return (
    <div className="flex flex-wrap gap-1.5">
      {phases.map((phase, i) => {
        const isFailed = !building && markedIndex >= 0 && i === markedIndex;
        const isActive = building && markedIndex >= 0 && i === markedIndex;
        const isDone = allDone || (markedIndex >= 0 && i < markedIndex);
        const isPending = !allDone && markedIndex >= 0 && i > markedIndex;
        const tone = isFailed
          ? "border-red-300 bg-red-50 text-red-700"
          : isActive
            ? "animate-pulse border-blue-300 bg-blue-50 text-blue-700"
            : isDone
              ? "border-green-200 bg-green-50 text-green-700"
              : isPending
                ? "border-border bg-muted text-subtle-foreground"
                : "border-border bg-muted text-muted-foreground";
        return (
          <span
            key={phase}
            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${tone}`}
          >
            {phase}
          </span>
        );
      })}
    </div>
  );
}

function MerchantRow({
  entry,
  phases,
}: {
  entry: DevMerchantResultEntry;
  phases: Phase[];
}) {
  const [purgeOpen, setPurgeOpen] = useState(false);
  const repo = useMemo(() => new MerchantsRepository(), []);

  return (
    <div className="border-border rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {entry.status === "created" && (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          )}
          {entry.status === "failed" && (
            <XCircle className="h-4 w-4 text-red-600" />
          )}
          {entry.status === "building" && (
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          )}
          <span className="text-foreground text-sm font-medium">
            Merchant {entry.index + 1}
          </span>
        </div>
        {entry.businessId && (
          <Link
            to={merchantPath(entry.businessId)}
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            View merchant →
          </Link>
        )}
      </div>

      <div className="mt-2">
        {/* failedPhase carries the current phase while status is "building";
            it only names the failure point once status is "failed". */}
        <PhaseChecklist
          phases={phases}
          failedPhase={entry.failedPhase}
          allDone={entry.status === "created"}
          building={entry.status === "building"}
        />
      </div>

      {(entry.email || entry.businessId) && (
        <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
          {entry.email && <CopyField label="Email" value={entry.email} />}
          {entry.password && (
            <CopyField label="Password" value={entry.password} mono />
          )}
          {entry.businessId && (
            <CopyField label="Business ID" value={entry.businessId} mono />
          )}
        </div>
      )}

      {entry.status === "failed" && entry.error && (
        <div className="mt-3 rounded bg-red-50 p-2">
          <p className="text-xs font-medium text-red-700">
            Failed at phase: {entry.failedPhase ?? "unknown"}
          </p>
          <pre className="mt-1 overflow-x-auto text-xs text-red-600">
            {entry.error}
          </pre>
        </div>
      )}

      {entry.status === "failed" && entry.businessId && (
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPurgeOpen(true)}
            className="text-red-700"
          >
            Purge partial merchant
          </Button>
          <PurgeMerchantDialog
            businessId={entry.businessId}
            businessName={null}
            open={purgeOpen}
            onOpenChange={setPurgeOpen}
            onPurged={() => setPurgeOpen(false)}
            repo={repo}
          />
        </div>
      )}
    </div>
  );
}

function CopyField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-muted-foreground mb-0.5">{label}</p>
      <div className="flex items-center gap-1">
        <code
          className={`bg-muted flex-1 truncate rounded px-1.5 py-1 ${mono ? "font-mono" : ""}`}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={() => copy(label, value)}
          className="text-subtle-foreground hover:bg-muted hover:text-foreground shrink-0 rounded p-1"
          title={`Copy ${label.toLowerCase()}`}
        >
          <Copy className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export function JobProgressPanel({
  job,
  createConnectedAccount,
  onReset,
}: Props) {
  const merchants = parseResult(job.result);
  const phases = expectedPhases(createConnectedAccount);
  const isTerminal =
    job.status === "complete" ||
    job.status === "failed" ||
    job.status === "cancelled";

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <JobStatusBadge status={job.status} />
        <span className="text-subtle-foreground text-xs">Job ID: {job.id}</span>
      </div>

      <ProgressBar processed={job.processed} total={job.total} />

      <CounterGrid raw={job.counters} />

      {job.status === "failed" && job.error && merchants.length === 0 && (
        <div className="rounded-md bg-red-50 p-3">
          <p className="text-xs font-medium text-red-700">Error</p>
          <pre className="mt-1 overflow-x-auto text-xs text-red-600">
            {job.error}
          </pre>
        </div>
      )}

      {merchants.length > 0 && (
        <div className="space-y-2">
          {merchants.map((entry) => (
            <MerchantRow key={entry.index} entry={entry} phases={phases} />
          ))}
        </div>
      )}

      {isTerminal && (
        <Button variant="outline" onClick={onReset} className="w-full">
          Start another
        </Button>
      )}
    </div>
  );
}
